import { useMemo } from "react";
import { Edge } from "reactflow";

/**
 * A generic edge processor hook that can:
 * - Update any edge fields
 * - Apply updates to all, specific, or a single edge by ID
 * - Provide default style and data normalization
 *
 * @param edges - The list of edges
 * @param options - Optional configuration:
 *   - update?: Partial<Edge> | ((edge: Edge) => Edge)
 *   - targetEdgeIds?: string | string[]
 */
const useUpdatedLocalEdgeValues = (
  edges: Edge[],
  options?: {
    update?: Partial<Edge> | ((edge: Edge) => Edge);
    targetEdgeIds?: string | string[];
  }
): Edge[] => {
  const { update, targetEdgeIds } = options || {};

  return useMemo(() => {
    const targetIds = Array.isArray(targetEdgeIds)
      ? targetEdgeIds
      : targetEdgeIds
      ? [targetEdgeIds]
      : null;

    return edges.map((edge) => {
      const colour_primary =
        (edge as any)?.data?.colour_primary ??
        (edge as any)?.data?.colorprimary ??
        (edge as any)?.style?.stroke ??
        "#000000";

      const colour_text =
        (edge as any)?.data?.colour_text ??
        (edge as any)?.style?.color ??
        "#ffffff";


      let newEdge: Edge = {
        ...edge,
        type: edge.type || "standardEdge",
        style: {
          ...(edge.style || {}),
          stroke: colour_primary,
          color: colour_text,
        },
        data: {
          ...edge.data,
          colour_primary,
          colour_text,
          animated: edge.animated ?? (edge.data as any)?.animated ?? false,
        },
      };


      // Apply update if provided
      if (update) {
        const shouldUpdate = !targetIds || targetIds.includes(edge.id);

        if (shouldUpdate) {
          if (typeof update === "function") {
            newEdge = update(newEdge);
          } else {
            newEdge = {
              ...newEdge,
              ...update,
              data: {
                ...newEdge.data,
                ...update.data,
              },
            };
          }
        }
      }

      return newEdge;
    });
  }, [edges, update, targetEdgeIds]);
};

export default useUpdatedLocalEdgeValues;
